import { ChangeEvent, useCallback, useState, useRef } from 'react';

import { Box, Stack, Typography } from '@mui/material';

import CreateDirectoryDialog from '@components/FileBrowser/Components/Header/CreateDirectoryDialog';
import FileBrowserBreadcrumbs from '@components/FileBrowser/Components/Header/FileBrowserBreadcrumbs';
import { useAddFilesToUpload } from '@components/FileBrowser/hooks';
import AddNewMenu from '@components/FileBrowser/Components/Header/AddNewMenu';
import { useCreateDirectory } from '@api/fileHandling/fileHandling';

interface IFileBrowserHeaderProps {
  location: string;
  showTitle?: boolean;
}

export const FileBrowserHeader = ({
  location,
  showTitle = false,
}: IFileBrowserHeaderProps) => {
  const [createNewDialogOpen, setCreateNewDialogOpen] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const createDirectory = useCreateDirectory();
  const addFilesToUpload = useAddFilesToUpload();

  const onAddNewMenuChange = (context: 'file' | 'directory') => {
    if (context === 'directory') {
      setCreateNewDialogOpen(true);
      return;
    }

    if (context === 'file') {
      inputRef.current?.click();
    }
  };

  const onFilesSelected = useCallback(
    (e: ChangeEvent<HTMLInputElement>) =>
      addFilesToUpload(e.currentTarget.files || [], location),
    [addFilesToUpload, location],
  );

  const handleCreate = useCallback(
    (directoryName: string) => {
      createDirectory.mutate({ dirPath: `${location}${directoryName}` });
    },
    [createDirectory, location],
  );

  return (
    <Stack sx={{ px: { xs: 2, md: 3 }, py: 1.5, gap: 1 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ gap: 1, flexWrap: 'wrap' }}
      >
        <input
          type="file"
          id="file"
          hidden
          ref={inputRef}
          onChange={onFilesSelected}
        />
        {createNewDialogOpen && (
          <CreateDirectoryDialog
            handleCreate={handleCreate}
            handleClose={() => setCreateNewDialogOpen(false)}
            location={location}
          />
        )}
        <Stack spacing={0.5} sx={{ minWidth: 0, flex: 1 }}>
          {showTitle && <Typography variant="h3">File browser</Typography>}
          <Typography variant="subtitle2">Current location</Typography>
          <Box sx={{ minWidth: 0, overflowX: 'auto', pb: 0.25 }}>
            <FileBrowserBreadcrumbs location={location} />
          </Box>
        </Stack>
        <AddNewMenu onChange={onAddNewMenuChange} />
      </Stack>
    </Stack>
  );
};
